/**
 * Permission codes matching the seeded permissions in the backend (see
 * CSC_CORE/Features/Authorization/Extensions/PermissionExtension.cs).
 * Used for route guards, component-level gating, and sidebar filtering.
 */
export const PERMISSIONS = {
  // Module Management (MODM)
  MOD_VIEW_MODULE_LIST: 'mod.view_module_list',
  MOD_EDIT_MODULE: 'mod.edit_module',

  // Authorization Management (AUTM)
  AUTH_VIEW_ROLE_LIST: 'auth.view_role_list',
  AUTH_CREATE_ROLE: 'auth.create_role',
  AUTH_EDIT_ROLE: 'auth.edit_role',
  AUTH_EDIT_ROLE_AUTH: 'auth.edit_role_auth',
  AUTH_VIEW_USER_ROLE_LIST: 'auth.view_user_role_list',
  AUTH_EDIT_USER_ROLE: 'auth.edit_user_role',

  // User Management (USEM)
  USER_CREATE_USER: 'user.create_user',
  USER_EDIT_USER_DETAILS: 'user.edit_user_details',
  USER_VIEW_USER_LIST: 'user.view_user_list',
  USER_CHANGE_USER_PASSWORD: 'user.change_user_password',
  USER_DEACTIVATE_USER: 'user.deactivate_user',

  // Issuance (ISSE)
  ISSUANCE_VIEW_REQUEST_LIST: 'issuance.view_request_list',
  ISSUANCE_SYNC_REQUESTS: 'issuance.sync_requests',
  ISSUANCE_VIEW_PICKLIST: 'issuance.view_picklist',
  ISSUANCE_VIEW_ALL_PICKLISTS: 'issuance.view_all_picklists',
  ISSUANCE_CREATE_PICKLIST: 'issuance.create_picklist',
  ISSUANCE_EDIT_PICKLIST: 'issuance.edit_picklist',
  ISSUANCE_FULFILL_PICKLIST: 'issuance.fulfill_picklist',
  ISSUANCE_POST_PICKLIST: 'issuance.post_picklist',
  ISSUANCE_CLOSE_PICKLIST: 'issuance.close_picklist',
  ISSUANCE_CANCEL_PICKLIST: 'issuance.cancel_picklist',
  // PW-32 / D35 — was PICKLIST_ADJUST: 'picklist.adjust', the one code in the app without its
  // module prefix, which is how it stayed invisible to anyone auditing what a role can do.
  ISSUANCE_ADJUST_PICKLIST: 'issuance.adjust_picklist',
  ISSUANCE_MANAGE_PICKLIST_PICKERS: 'issuance.manage_picklist_pickers',
  ISSUANCE_VIEW_DASHBOARD: 'issuance.view_dashboard',

  // Receiving (RECV)
  RECEIVING_VIEW: 'receiving.view',
  RECEIVING_VIEW_ALL: 'receiving.view_all',
  RECEIVING_CREATE: 'receiving.create',
  RECEIVING_POST: 'receiving.post',
  RECEIVING_ADJUST: 'receiving.adjust',

  // Release Notes (MODM)
  RELEASE_MANAGE_RELEASES: 'release.manage_releases',

  // Quotations (QUOT)
  QUOTATIONS_VIEW_QUOTATION: 'quotations.view_quotation',
  QUOTATIONS_VIEW_ALL_QUOTATIONS: 'quotations.view_all_quotations',
  QUOTATIONS_CREATE_QUOTATION: 'quotations.create_quotation',
  QUOTATIONS_POST_CUTTING_LIST: 'quotations.post_cutting_list',
  QUOTATIONS_REVIEW_INBOX: 'quotations.review_inbox',
  QUOTATIONS_SEND_QUOTATION: 'quotations.send_quotation',
  QUOTATIONS_ARCHIVE_QUOTATION: 'quotations.archive_quotation',
  QUOTATIONS_DELETE: 'quotations.delete',

  // Sales Orders (SORD) — FO-SOI-1 set. Single `view` permission (no own/all
  // split). No `restore` — archive is one-way (auto-tombstone after retention).
  SALES_ORDERS_VIEW_SALES_ORDER: 'sales_orders.view_sales_order',
  SALES_ORDERS_CREATE_SALES_ORDER: 'sales_orders.create_sales_order',
  SALES_ORDERS_SUBMIT_SALES_ORDER: 'sales_orders.submit_sales_order',
  SALES_ORDERS_ARCHIVE_SALES_ORDER: 'sales_orders.archive_sales_order',
  SALES_ORDERS_DELETE_SALES_ORDER: 'sales_orders.delete_sales_order',

  // Announcements (ANNT)
  ANNOUNCEMENT_PUBLIC_VIEW: 'announcement.public_view',
  ANNOUNCEMENT_VIEW: 'announcement.view',
  ANNOUNCEMENT_CREATE: 'announcement.create',
  ANNOUNCEMENT_EDIT: 'announcement.edit',
  ANNOUNCEMENT_DELETE: 'announcement.delete',

  // Activity (ACTV) — cross-module audit feed
  ACTIVITY_VIEW_ALL: 'activity.view_all',

  // Master-data webhook queue ops (narrowed in 86d33u348). Gates webhook queue
  // stats + dead-letter replay on /api/admin/webhook/*. NOT the dialog's sync
  // buttons — those are on QUOTATIONS_SYNC_MASTER_DATA.
  QUOTATIONS_MANAGE_WEBHOOK_QUEUE: 'quotations.manage_webhook_queue',

  // Sync master-data caches (86d33u348). Gates the Sync + Full Sync buttons on
  // MasterDataConfigDialog and the /sync-master-data/* + /api/admin/cache/*
  // endpoints. Grant to roles that should be able to manually trigger
  // NetSuite → DOP cache refresh.
  QUOTATIONS_SYNC_MASTER_DATA: 'quotations.sync_master_data',

  // Edit AI extraction config (86d33u348). Tight scope: gates the
  // primary/fallback Gemini model edit Save button in the dialog and
  // PUT /api/configuration/gemini.
  QUOTATIONS_EDIT_AI_CONFIG: 'quotations.edit_ai_config',

  // Async work outbox admin ops (86d3mnuxe). Gates /api/admin/outbox/* (replay a
  // dead-lettered job, cancel one) — FE-side it gates the Cancel button on posting
  // failure cards (only offered when the post never reached NetSuite).
  SYSTEM_MANAGE_OUTBOX: 'system.manage_outbox',

  // Read-only System Health dashboard (86d3peur0). Gates GET /api/admin/health/* and,
  // FE-side, the /system-health route + its sidebar nav item. The dead-letter Requeue
  // action inside it still requires SYSTEM_MANAGE_OUTBOX above.
  SYSTEM_VIEW_HEALTH: 'system.view_health',

  // Document Capture (86d3rrf3e, module DSCN). View rights are PER DOCUMENT FAMILY: a family
  // you cannot view does not exist for you — not in the registry, the counts, the live stream
  // or the viewer, and the API answers the same 404 it gives a document that isn't there.
  // OP/DV/JV are treasury tier (bank + payment detail, and GL accounts on a JV) — grant those
  // narrowly; every read of one is written to an append-only audit trail.
  DOCSCAN_VIEW_DR: 'docscan.view_dr',
  DOCSCAN_VIEW_INV: 'docscan.view_inv',
  DOCSCAN_VIEW_INT: 'docscan.view_int',
  DOCSCAN_VIEW_CM: 'docscan.view_cm',
  DOCSCAN_VIEW_OP: 'docscan.view_op',
  DOCSCAN_VIEW_DV: 'docscan.view_dv',
  DOCSCAN_VIEW_JV: 'docscan.view_jv',

  // Upload scans (Scan Station watcher or manual drop). The scan-station machine account holds
  // ONLY this one — FE-side it gates the station card, the drop zone and the heartbeat, which
  // would otherwise 403-loop for a view-only user.
  DOCSCAN_UPLOAD: 'docscan.upload',

  // Work the review queue (assign numbers, keep-watching, reject, resolve conflicts, approve
  // replacements). Still narrowed by the per-family view rights above; items whose family is
  // unknown are shown only to reviewers who can see every treasury family.
  DOCSCAN_REVIEW: 'docscan.review',

  // Administrative: reject-and-unlink a document already linked in NetSuite, manage the prefix
  // registry, re-drive quarantined files.
  DOCSCAN_MANAGE: 'docscan.manage',

  // Mint/revoke public customer links (/p/{token}) for INV/DR — a distinct capability from viewing
  // internally. FE gates the "Copy public link" affordance; the server is the authority.
  DOCSCAN_SHARE: 'docscan.share',

  // 86d3x48q7 (T13) — the "Second reader" surface: turn AI verification on or off, pick the reader
  // level, set the spot-check rate, and read the calibration figures. Separate from DOCSCAN_MANAGE
  // because it changes how documents get IDENTIFIED for everyone, which is a different kind of
  // authority from re-driving a stuck file. Underscore, not a dot — two segments is the convention
  // every other code here follows, and the permission checker splits on the first dot.
  DOCSCAN_AI_MANAGE: 'docscan.ai_manage',
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
